'use client';

/**
 * GRS search service
 * Each search service needs a URL constructor and a response parser.
 */
import { getNumberOfPages } from '../utils';
import type { ReadonlyURLSearchParams } from 'next/navigation';

import {
    GRS_BOPIM_FACET_KEY,
    GRS_BUY_IN_WAREHOUSE_FACET_KEY,
    GRS_CATEGORY_URI_FACET_KEY,
    GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
    GRS_WAREHOUSE_PICKUP_FACET_KEY,
} from '@/components/SearchResultsFacets/grs.constants';
import {
    grsBooleanValueFacets,
    grsRangeValueFacets,
} from '@/components/SearchResultsFacets/grs.lib';
import { PageTypes } from '@/constants/index';
import { getKeyValue } from '@/services/content/data';
import { GRS_PRICE_FACET_WAREHOUSE_KEY } from '@/services/search/services/grs.constants';
import type {
    GrsConfig,
    SearchPageConfigProperties,
} from '@/src/types/contentStack';
import type {
    ContentStackEntryDataProps,
    GRSFacetConfig,
    GRSFacetConfigProperties,
} from '@/types/contentStack';
import { splitByCommaOutsideParentheses } from '@/utils/searchFacets';
import type { LocaleProps } from '@nxt-costco-com/forge-digital-components';

import { SearchResultGRS, WarehouseSearchParams } from './GRS.types';

const grsKeyQueryMap: Record<string, (locationId: string) => string> = {
    [GRS_SHOW_OUT_OF_STOCK_FACET_KEY]: () => 'SHOW_OUT_OF_STOCK',
    [GRS_BOPIM_FACET_KEY]: mdoLocationId =>
        `(attributes.program_types: ANY(\"LTLPickUp\") AND (inventory(${mdoLocationId}, attributes.availability): ANY(\"IN_STOCK\", \"LOW_STOCK\", \"BACK_ORDER\", \"PRESELL\", \"SHIP_RESTRICTED\") OR (attributes.disp_on_zero_inv = 1 AND inventory(${mdoLocationId}, attributes.availability): ANY(\"OUT_OF_STOCK\")) ))`,
    [GRS_BUY_IN_WAREHOUSE_FACET_KEY]: warehouseId =>
        `(attributes.program_types: ANY(\"InWarehouse\") AND (inventory(${warehouseId}, attributes.availability): ANY(\"IN_STOCK\", \"LOW_STOCK\", \"BACK_ORDER\", \"PRESELL\", \"SHIP_RESTRICTED\") OR (attributes.disp_on_zero_inv = 1 AND inventory(${warehouseId}, attributes.availability): ANY(\"OUT_OF_STOCK\")) ))`,
    [GRS_WAREHOUSE_PICKUP_FACET_KEY]: warehouseId =>
        `((attributes.program_types: ANY("UseWarehouseInventory") AND (inventory(${warehouseId}, attributes.availability): ANY("IN_STOCK", "LOW_STOCK", "BACK_ORDER", "PRESELL", "SHIP_RESTRICTED") OR (attributes.disp_on_zero_inv = 1 AND inventory(${warehouseId}, attributes.availability): ANY("OUT_OF_STOCK")))) OR attributes.delivery_type: ANY(\"Warehouse Pick-up\") OR attributes.delivery_type: ANY(\"Cueillette à l’entrepôt\"))`,
};

let cachedQueryParamKeyToFacetKeyMap: Record<string, string> | null = null;

export const createQueryParamKeyToFacetKeyMap = (
    grsFacetConfig: GRSFacetConfig,
): Record<string, string> => {
    if (cachedQueryParamKeyToFacetKeyMap) {
        return cachedQueryParamKeyToFacetKeyMap;
    }

    const queryParamKeyToFacetKeyMap: Record<string, string> = {};
    for (const [key, value] of Object.entries(grsFacetConfig)) {
        queryParamKeyToFacetKeyMap[value.queryParamKey ?? key] = key;
    }

    cachedQueryParamKeyToFacetKeyMap = queryParamKeyToFacetKeyMap;
    return queryParamKeyToFacetKeyMap;
};

export const resetQueryParamKeyToFacetKeyMapCache = () => {
    cachedQueryParamKeyToFacetKeyMap = null;
};

export const getFacetKeyFromQueryParamKey = (
    queryParamKey: string,
    grsFacets: GRSFacetConfig,
) => {
    const queryParamKeyToFacetKeyMap =
        createQueryParamKeyToFacetKeyMap(grsFacets);
    const facetKey = queryParamKeyToFacetKeyMap[queryParamKey];
    return facetKey;
};

export const getGRSFacetsFromConfig = (
    grsFacetConfig: ContentStackEntryDataProps<GRSFacetConfigProperties>,
): GRSFacetConfig => {
    const grsAttributesFacets = grsFacetConfig?.properties?.[0]?.attributes;
    const updatedAttributesFacets: Record<string, any> = {};

    if (grsAttributesFacets) {
        // Update the keys to include the 'attributes.' prefix so they align with the GRS API
        // Contentstack would not allow a period in the key name so we have to add it here
        for (const [key, value] of Object.entries(grsAttributesFacets)) {
            const updatedKey = `attributes.${key}`;
            updatedAttributesFacets[updatedKey] = value;
        }
    }

    return {
        ...grsFacetConfig?.properties?.[0]?.standard,
        ...updatedAttributesFacets,
    };
};

const getOrderBy = (searchParams: ReadonlyURLSearchParams) => {
    const sortBy = searchParams.get('sortBy');

    switch (sortBy) {
        case 'score+desc': {
            return null;
        }
        case 'item_location_pricing_salePrice+desc': {
            return 'price desc';
        }
        case 'item_location_pricing_salePrice+asc': {
            return 'price';
        }
        case 'item_ratings+desc': {
            return 'rating desc';
        }
        case 'item_startDate+desc': {
            return 'attributes.start_date_epoch desc';
        }
        case 'item_page_views+desc': {
            return null;
        }
        default: {
            return null;
        }
    }
};

export const getGRSQueryRangeValue = (key: string, values: string[]) => {
    // Convert the string tuples into number pairs
    const parsedRanges = values
        .map(value => {
            const trimmedValue = value.trim();
            const match = trimmedValue.match(/\((\d+),(\d+)\)/);
            if (match && match[1] !== undefined && match[2] !== undefined) {
                return [parseInt(match[1], 10), parseInt(match[2], 10)];
            }
            return null;
        })

        .filter((item): item is [number, number] => item !== null) as [
        number,
        number,
    ][];

    // Sort ranges by the start value
    parsedRanges.sort((a, b) => a[0] - b[0]);

    const consolidated: [number, number][] = [];

    for (const [start, end] of parsedRanges) {
        const lastRange = consolidated.at(-1);
        if (lastRange && lastRange[1] >= start) {
            // Merge overlapping or adjacent ranges
            lastRange[1] = Math.max(lastRange[1], end);
        } else {
            consolidated.push([start, end]);
        }
    }

    // Format the output string
    return consolidated
        .map(([s, e]) => `${key}: IN(${s}i, ${e}i)`)
        .join(' OR ');
};

export const getGRSFilterBy = ({
    searchParams,
    whloc,
    grsFacetConfig,
    categoryPageId,
    mdoLocationId = '',
}: {
    searchParams: ReadonlyURLSearchParams | undefined;
    whloc: string | undefined;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    categoryPageId: string | undefined;
    mdoLocationId: string | undefined;
}) => {
    if (!searchParams || !grsFacetConfig) return [];

    const grsFilterParams = new URLSearchParams();
    const grsFacets = getGRSFacetsFromConfig(grsFacetConfig);
    const grsFilterParamKeys = Object.keys(grsFacets);

    searchParams.forEach((value, queryParamKey) => {
        const facetKey = getFacetKeyFromQueryParamKey(queryParamKey, grsFacets);
        if (facetKey && grsFilterParamKeys.includes(facetKey)) {
            grsFilterParams.append(facetKey, value);
        }
    });

    if (categoryPageId) {
        grsFilterParams.append(GRS_CATEGORY_URI_FACET_KEY, categoryPageId);
    }

    const getGRSQueryDynamicValue = (key: string, values: string[]) => {
        const formattedValues = values.map(value => `\"${value}\"`).join(', ');
        return `${key}: ANY(${formattedValues})`;
    };

    const getGRSQueryValue = (key: string, values: string[]) => {
        if (grsRangeValueFacets.includes(key)) {
            return getGRSQueryRangeValue(key, values);
        } else {
            return getGRSQueryDynamicValue(key, values);
        }
    };

    const grsFilterParamsEntries = Array.from(grsFilterParams.entries());

    const grsProgramTypeFacetKeys = [
        GRS_BOPIM_FACET_KEY,
        GRS_BUY_IN_WAREHOUSE_FACET_KEY,
        GRS_WAREHOUSE_PICKUP_FACET_KEY,
    ];

    const filterBy: string[] = [];

    // Program Type Filters separated so they can be processed as single filter with OR condition
    const programTypeFilters = [];

    for (const [key, value] of grsFilterParamsEntries) {
        const values = splitByCommaOutsideParentheses(value);
        const filterValues: string[] = [];

        if (grsBooleanValueFacets.includes(key)) {
            const isTrue = values[0] === 'true';
            if (isTrue && (whloc || mdoLocationId) && grsKeyQueryMap[key]) {
                const locationId =
                    key === GRS_BOPIM_FACET_KEY
                        ? mdoLocationId
                        : (whloc as string);
                const query = grsKeyQueryMap[key](locationId);
                if (grsProgramTypeFacetKeys.includes(key)) {
                    programTypeFilters.push(query);
                } else {
                    filterValues.push(query);
                }
            }
        } else if (values.length > 0) {
            filterValues.push(getGRSQueryValue(key, values));
        }

        if (filterValues.length) {
            filterBy.push(filterValues.join(' OR '));
        }
    }

    if (programTypeFilters.length) {
        filterBy.push(programTypeFilters.join(' OR '));
    }

    return filterBy;
};

const generateRequestBody = (
    grsConfig: GrsConfig,
    grsFacetConfig: ContentStackEntryDataProps<GRSFacetConfigProperties>,
    searchParams: ReadonlyURLSearchParams,
    currentPage: number,
    adobeSessionId: string,
    { loc, mdo, userLocation, userZipCode, whloc }: WarehouseSearchParams,
    categoryPageId: string | undefined,
    resultsPerPage: number,
    isGRSPersonalizationEnabled: boolean,
    userInfoId: string | undefined,
) => {
    let body = grsConfig?.required_request_parameters;
    const keyword = searchParams.get('keyword');

    const filterBy = getGRSFilterBy({
        searchParams,
        whloc,
        grsFacetConfig,
        categoryPageId,
        mdoLocationId: mdo,
    });
    const priceFacetWarehouse = getKeyValue(
        GRS_PRICE_FACET_WAREHOUSE_KEY,
        grsFacetConfig,
    );

    const orderBy = getOrderBy(searchParams);

    const pageCategories = categoryPageId ? [categoryPageId] : [];

    const locations = loc?.split(',') || [];
    // "*" used in LW but causes error in GRS query
    const filteredLocations = locations.filter(loc => loc !== '*');

    const deliveryLocations = priceFacetWarehouse
        ? [priceFacetWarehouse, ...filteredLocations]
        : filteredLocations;

    const offset = resultsPerPage * (currentPage - 1);

    body = {
        ...body,
        deliveryLocations,
        filterBy: filterBy ?? [],
        // TODO - Remove conditional addition of mdoLocationId property once API is updated to allow null values without causing error
        ...(mdo ? { mdoLocationId: mdo } : {}),
        pageSize: resultsPerPage,
        offset: offset,
        orderBy: orderBy,
        pageCategories,
        personalizationEnabled:
            body.personalizationEnabled || isGRSPersonalizationEnabled,
        query: keyword ?? '',
        searchMode: 'page',
        shipToPostal: userZipCode,
        shipToState: userLocation,
        visitorId: adobeSessionId,
        ...(userInfoId ? { userInfo: { userId: userInfoId } } : {}),
        warehouseId: whloc,
    };
    return JSON.stringify(body);
};

/**
 * GRS Search Service.
 */

export async function GrsQuery(
    grsConfig: GrsConfig | undefined,
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined,
    searchParams: ReadonlyURLSearchParams,
    lang: LocaleProps,
    properties: SearchPageConfigProperties,
    { loc, mdo, whloc, userLocation, userZipCode }: WarehouseSearchParams,
    pageType: string,
    site: string,
    adobeSessionId: string,
    categoryPageId: string | undefined,
    searchFsaExemptKeywords: string[] | undefined,
    categoryFsaFlag: boolean,
    isGRSPersonalizationEnabled: boolean,
    userInfoId: string | undefined,
): Promise<any> {
    //return value should be updated later depending on how we want the response
    let returnValue: SearchResultGRS = {} as SearchResultGRS;
    const { resultsPerPage } = properties;

    if (!grsConfig || !grsFacetConfig) {
        return returnValue;
    }

    if (!searchParams.get('keyword') && pageType === PageTypes.SEARCH) {
        return returnValue;
    }

    const currentPage = Number(searchParams.get('currentPage')) || 1;

    const formattedLocale = lang
        .split('-')
        .map((part: string, index: number) => {
            if (index === 1) {
                return part.toUpperCase(); // Only capitalize the second part
            }
            return part;
        })
        .join('-');
    const requestHeaders = {
        ...grsConfig.required_request_headers,
        locale: formattedLocale,
        client_id: site,
    };
    const body = generateRequestBody(
        grsConfig,
        grsFacetConfig,
        searchParams,
        currentPage,
        adobeSessionId,
        {
            loc,
            mdo,
            userLocation,
            userZipCode,
            whloc,
        },
        categoryPageId,
        resultsPerPage,
        isGRSPersonalizationEnabled,
        userInfoId,
    );

    const response = await fetch(grsConfig?.endpoint, {
        method: 'POST',
        headers: {
            ...requestHeaders,
        },
        body: body,
    });

    if (!response.ok) {
        console.error('GRS ERROR', response.statusText);
        return returnValue;
    }
    let data: SearchResultGRS = await response.json();

    const pagination = {
        currentPage: currentPage,
        totalDocs: data?.searchResult?.totalSize,
        totalPages: getNumberOfPages(
            data?.searchResult?.totalSize || 0,
            resultsPerPage,
        ),
    };

    const resultsMappedWithInventory = data?.searchResult?.results?.map(
        (result: any) => {
            const matchingInventory = data.inventoryResponse?.find(
                inventory => inventory.productId === result.id,
            );
            return {
                ...result,
                inventory: matchingInventory,
                mdoLocation: data.mdoLocation,
            };
        },
    );

    returnValue = {
        ...data,
        pagination,
        searchResult: {
            ...data?.searchResult,
            results: resultsMappedWithInventory,
        },
        metrics: {
            semantic: false,
        },
    };

    const searchFacets = data?.searchResult?.facets;
    const doesFsaFacetExist =
        (
            searchFacets?.find(facet => facet.key === 'attributes.fsa_eligible')
                ?.values || []
        ).length > 0;

    const doesChdiFacetExist = !!searchFacets?.find(
        facet => facet.key === 'attributes.chdi_eligible',
    )?.values?.length;

    returnValue.isAdTargetingExplicitlyDisabled =
        doesFsaFacetExist || doesChdiFacetExist;
    returnValue.isAdTargetingExceptionEnabled = Boolean(
        searchFsaExemptKeywords?.includes(searchParams.get('keyword') || '') ||
            categoryFsaFlag,
    );
    // adding new redirect property to the return value so that we can follow LucidWorks like structure
    returnValue.redirect = data?.searchResult?.redirectUri ?? '';
    returnValue.isAdTargetingEnabled =
        returnValue.isAdTargetingExceptionEnabled ||
        !returnValue.isAdTargetingExplicitlyDisabled;
    returnValue.metrics.correction = data.searchResult?.correctedQuery;
    returnValue.metrics.original =
        (data.searchResult?.correctedQuery && searchParams.get('keyword')) ||
        '';
    returnValue.metrics.semantic = false;
    // !!data.searchResult?.queryExpansionInfo?.expandedQuery;
    return returnValue;
}

export const isSearchResultGRS = (result: any): result is SearchResultGRS => {
    return result && Array.isArray(result?.searchResult?.results);
};

export const getSearchResultsType = (searchResult: SearchResultGRS) => {
    const searchResultData = searchResult?.searchResult;

    if (!searchResultData) return null;

    const { correctedQuery, queryExpansionInfo, redirectUri, totalSize } =
        searchResultData;

    if (correctedQuery) return 'corrected';
    if (queryExpansionInfo?.expandedQuery) return 'loosened';
    if (totalSize === 0) return 'zero';
    if (redirectUri) return 'redirect';

    return 'expected';
};
